import { useEffect, useMemo, useState } from 'react'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useParams } from 'react-router'

import { useFetchList, useReload } from 'store'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'

import { camelToTitle } from '../../../utils'

import RulesEditor from './RulesEditor'
import useRulesMetadata from './useRulesMetadata'

import styles from './RulesEditorController.module.sass'

import {
	fields,
	eventsSubscriptionOutput,
	getEventsSubscriptionRulesSchema,
	numericOperators,
	listOperators,
	stringOperators,
	TaskTypes,
	RuleTypes,
	coordinators,
	dataChangeActions,
} from './RuleSchema'
import Rule from 'models/Rule'
import { translateErrorMessage } from 'hooks/useSubmitTask'

const FORM_ID = 'EDITOR_FORM_ID'

const ruleActions = (i18n) =>
	dataChangeActions.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const ruleEntities = (i18n, dataChangeEntities) =>
	dataChangeEntities.map((value) => ({
		value,
		text: i18n.translate(camelToTitle(value)),
	}))

const availableFields = (i18n, customFields) =>
	(customFields ?? fields).map((value) => ({
		value,
		text: i18n.translate(camelToTitle(value)),
	}))

const availableNumericOperators = (i18n) =>
	numericOperators.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableListOperators = (i18n) =>
	listOperators.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableStringOperators = (i18n) =>
	stringOperators.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const availableCoordinators = (i18n) =>
	coordinators.map((value) => ({
		value,
		text: i18n.translate(`${value}Coordinator`),
	}))

const availableOutput = (i18n) =>
	eventsSubscriptionOutput.map((value) => ({
		value,
		text: i18n.translate(value),
	}))

const EventsSubscriptionsController = (props) => {
	const { i18n, expireSession, pushNotification } = useGlobal()
	const { ruleType = props.ruleType, taskType = props.taskType } = useParams()
	const isSystemNotifications = taskType === 'system-notifications'
	const subscriptionType = isSystemNotifications ? 'system' : 'user'
	const apiEndpoint = `/events_subscription/${subscriptionType}`
	const apiEntitiesFieldsEndpoint = `/events_subscription/entities_fields`
	const [{ endpoint, initialData }, setInitialData] = useState({
		endpoint: '',
		initialData: [],
	})
	const [submitError, setSubmitError] = useState(null)

	const metadata = useRulesMetadata()
	const reload = useReload()
	const { contents, state } = useFetchList(
		[apiEndpoint, apiEntitiesFieldsEndpoint],
		[
			(raw) => {
				return Rule.fromJSON({
					...raw,
					ruleType: 'EventsSubscriptions',
					taskType: raw.type,
				})
			},
			(raw) => {
				const entityFields = raw.entities?.reduce(
					(result, { fields, entity }) => {
						result[entity] = fields
						return result
					},
					{}
				)
				return {
					...entityFields,
					context: raw.context.fields ?? [],
				}
			},
		]
	)

	const rulesRaw = useMemo(
		() =>
			state === 'hasValue' && Array.isArray(contents[0])
				? contents[0]
				: [],
		[contents, state]
	)

	const defaultValues = useMemo(
		() => ({
			rules: Array.isArray(contents[0])
				? JSON.parse(
						JSON.stringify(
							[...contents[0]].sort((a, b) => a.order - b.order)
						)
				  )
				: [],
		}),
		[contents]
	)

	const entitiesFields = useMemo(() => contents[1] ?? {}, [contents])
	const dataChangeEntities = Object.keys(entitiesFields).filter(
		(key) => key !== 'context'
	)

	const entityFieldsOptions = useMemo(() => {
		const contextFields = availableFields(
			i18n,
			entitiesFields['context'] ?? []
		)
		return dataChangeEntities.reduce((result, entity) => {
			const translatedFields =
				availableFields(i18n, entitiesFields[entity]) ?? []
			result[entity.toLocaleLowerCase()] = {
				INSERT: {
					fields: {
						Entity: translatedFields,
						Context: contextFields,
					},
				},
				UPDATE: {
					fields: {
						OldEntity: translatedFields,
						Entity: translatedFields,
						Context: contextFields,
					},
				},
				DELETE: {
					fields: {
						Entity: translatedFields,
						Context: contextFields,
					},
				},
			}
			return result
		}, {})
	}, [i18n, dataChangeEntities, entitiesFields])

	const numericOperatorsOptions = useMemo(
		() => availableNumericOperators(i18n),
		[i18n]
	)
	const listOperatorsOptions = useMemo(
		() => availableListOperators(i18n),
		[i18n]
	)
	const stringOperatorsOptions = useMemo(
		() => availableStringOperators(i18n),
		[i18n]
	)
	const coordinatorsOptions = useMemo(
		() => availableCoordinators(i18n),
		[i18n]
	)

	const outputOptions = useMemo(() => availableOutput(i18n), [i18n])
	const ruleActionsOptions = useMemo(() => ruleActions(i18n), [i18n])
	const ruleEntitiesOptions = useMemo(
		() => ruleEntities(i18n, dataChangeEntities),
		[dataChangeEntities, i18n]
	)

	const options = {
		...metadata,
		fields: [],
		users: metadata.notificationUsers,
		numericOperators: numericOperatorsOptions,
		listOperators: listOperatorsOptions,
		stringOperators: stringOperatorsOptions,
		outputOptions,
		coordinatorsOptions,
		ruleActions: ruleActionsOptions,
		ruleEntities: ruleEntitiesOptions,
		entities: entityFieldsOptions,
	}

	const methods = useForm({
		resolver: zodResolver(getEventsSubscriptionRulesSchema(options, i18n)),
		shouldUseCustomValidity: true,
		defaultValues,
	})

	const { reset, formState, handleSubmit } = methods

	useEffect(
		() => () => {
			setInitialData({ endpoint: '', initialData: [] })
		},
		[]
	)

	useEffect(() => {
		if (
			endpoint !== apiEndpoint &&
			state === 'hasValue' &&
			Array.isArray(contents[0])
		) {
			setInitialData({
				endpoint: apiEndpoint,
				initialData: contents[0],
			})
		}
	}, [contents.length, endpoint, rulesRaw, state, contents, apiEndpoint])

	useEffect(() => {
		reset({ rules: initialData })
	}, [initialData, reset])

	const onSubmit = (formData) => {
		const rulesForSubmission = formData.rules.map((rule) =>
			Rule.toJSON({ ...rule, ruleType: rule.taskType })
		)

		setSubmitError(null)
		const endpoint = `${API_URL}/api/events_subscription`

		return fetch(
			endpoint,
			fetchOptions({
				method: 'PUT',
				body: {
					ruleType: TaskTypes[taskType], // EVENT SUBSCRIPTIONS API only needs subtype
					rules: rulesForSubmission,
				},
			})
		)
			.then((r) => parseJSONContent(r))
			.then((r) => {
				setSubmitError(null)
				reload([apiEndpoint])
				pushNotification(
					undefined,
					'success',
					i18n`Rule created successfully!`
				)
			})
			.catch((e) => {
				if (e.status === '401' || e.status === 401) {
					expireSession()
				} else {
					const errorMessage = translateErrorMessage(
						e?.formErrors?.[0] ?? e?.fieldErrors?.[0] ?? e,
						i18n
					)

					setSubmitError(errorMessage)
				}
			})
	}

	const isSubmitting = useMemo(
		() => formState.isSubmitting,
		[formState.isSubmitting]
	)

	return (
		<>
			<span className={styles['background']} />
			<div
				className={`${styles['actions-container']} ${
					isSubmitting ? styles['--is-submitting'] : ''
				} ${submitError ? styles['--has-error'] : ''}`}
			>
				{isSubmitting && (
					<span className={styles['progress-background']} />
				)}
				<HStack justify='space-between'>
					<span>
						<span className={styles['title']}>
							<h4>{i18n`Events subscription`} -</h4>
							<h5>
								{isSystemNotifications
									? i18n`System notifications`
									: i18n`User notifications`}
							</h5>
						</span>
						<small>
							{isSubmitting ? i18n`Submitting` : submitError}
						</small>
					</span>
					<HStack gap='small'>
						<ActionButton
							type='button'
							onClick={() => {
								const isDirty =
									Object.entries(formState.dirtyFields)
										.length > 0
								if (isDirty) {
									reset(defaultValues)
								}
							}}
						>
							{i18n`Reset`}
						</ActionButton>
						<ActionButton
							primary
							type='submit'
							form={FORM_ID}
							disabled={isSubmitting}
						>
							{isSubmitting ? i18n`Submitting` : i18n`Save`}
						</ActionButton>
					</HStack>
				</HStack>
			</div>
			<div className={styles['editor']}>
				<FormProvider {...methods}>
					<RulesEditor
						id={FORM_ID}
						ignoreRulerOrder={true}
						scrollableClassName={styles['editor']}
						ruleType={RuleTypes[ruleType]}
						taskType={TaskTypes[taskType]}
						outputDefault={{
							type: 'UserIds',
						}}
						options={options}
						onSubmit={handleSubmit(onSubmit)}
					/>
				</FormProvider>
			</div>
		</>
	)
}

export default EventsSubscriptionsController
